import { ResultRow } from '../api/ForecastApi';

/**
 * A single detected divergence between consecutive forecast runs (fcds)
 * for the same target_date.
 */
export interface DivergencePoint {
  fg: number;
  targetDate: string;
  fcd: string;
  previousFcd: string;
  previousP50: number;
  currentP50: number;
  absoluteChange: number;
  percentageChange: number;
}

/**
 * Complete divergence analysis result returned by computeDivergence.
 */
export interface DivergenceResult {
  rows: ResultRow[];
  divergencePoints: DivergencePoint[];
  targetDates: string[];
  topDivergentTargetDates: string[];
}

/**
 * Compute divergence points from forecast result rows.
 *
 * Groups rows by target_date, sorts each group by fcd ascending, then compares
 * consecutive pairs. A pair is flagged as a DivergencePoint when:
 *   ABS(curr_p50 - prev_p50) / (prev_p50 + 1) > threshold
 *
 * The +1 in the denominator prevents division by zero when prev_p50 is 0.
 *
 * @param rows - Raw result rows from the divergence query
 * @param threshold - Relative change threshold to flag divergence (default 0.20 = 20%)
 */
export function computeDivergence(
  rows: ResultRow[],
  threshold = 0.20
): DivergenceResult {
  // Group rows by target_date
  const groupedByTarget = new Map<string, ResultRow[]>();
  for (const row of rows) {
    const targetDate = String(row['target_date'] ?? '');
    if (!groupedByTarget.has(targetDate)) {
      groupedByTarget.set(targetDate, []);
    }
    groupedByTarget.get(targetDate)!.push(row);
  }

  // Sort each group by fcd ascending
  for (const group of groupedByTarget.values()) {
    group.sort((a, b) => String(a['fcd'] ?? '').localeCompare(String(b['fcd'] ?? '')));
  }

  const divergencePoints: DivergencePoint[] = [];
  const targetDates = Array.from(groupedByTarget.keys()).sort();

  // For each target_date group, compare consecutive pairs
  for (const [targetDate, group] of groupedByTarget) {
    for (let i = 1; i < group.length; i++) {
      const prev = group[i - 1];
      const curr = group[i];

      const prevP50 = Number(prev['p50_raw'] ?? 0);
      const currP50 = Number(curr['p50_raw'] ?? 0);
      const absoluteChange = Math.abs(currP50 - prevP50);
      const percentageChange = absoluteChange / (prevP50 + 1);

      if (percentageChange > threshold) {
        divergencePoints.push({
          fg: Number(curr['fg'] ?? 0),
          targetDate,
          fcd: String(curr['fcd'] ?? ''),
          previousFcd: String(prev['fcd'] ?? ''),
          previousP50: prevP50,
          currentP50: currP50,
          absoluteChange,
          percentageChange,
        });
      }
    }
  }

  const topDivergentTargetDates = getTopDivergentTargetDates(divergencePoints);

  return {
    rows,
    divergencePoints,
    targetDates,
    topDivergentTargetDates,
  };
}

/**
 * Select the top N target_dates by their maximum divergence (percentageChange).
 *
 * Groups divergence points by targetDate, finds the maximum percentageChange
 * within each group, then returns the top N target_dates sorted descending
 * by that maximum.
 *
 * @param points - Array of divergence points to analyze
 * @param max - Maximum number of target_dates to return (default 10)
 */
export function getTopDivergentTargetDates(
  points: DivergencePoint[],
  max = 10
): string[] {
  // Group by targetDate, track max percentageChange per group
  const maxByTarget = new Map<string, number>();
  for (const point of points) {
    const current = maxByTarget.get(point.targetDate) ?? 0;
    if (point.percentageChange > current) {
      maxByTarget.set(point.targetDate, point.percentageChange);
    }
  }

  // Sort by max divergence descending, return top N
  return Array.from(maxByTarget.entries())
    .sort((a, b) => b[1] - a[1])
    .slice(0, max)
    .map(([targetDate]) => targetDate);
}
